import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {Pagination} from '../../shared/Models/Pagination';
import {Helper} from '../../helpers/helper.service';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {DealerType} from '../Models/DealerType';
import { catchError, map, tap } from 'rxjs/operators';
import { CookieManagerService } from 'src/app/shared/Services/cookie.service';

/** Language list response */
export interface DealerTypeListResponse {
  total: number;
  count: number;
  per_page: number;
  types: DealerType[];
}

export interface ActivateTwoFactorResponse {
  qrCodeContent: string;
  qrCodeSecret: string;
  qrCodeUrl: string;
}

@Injectable()
export class UsersService {
  constructor(private helper: Helper, private http: HttpClient, private cookieManager: CookieManagerService) {
  }


  public getUsers(gridData) {
    return this.http.get<Pagination<any>>(environment.apiUrl + '/backoffice/users/backoffice/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getUser(userId) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/users/backoffice/' + userId)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getCurrentUser() {
    return this.http.get<any>(environment.apiUrl + '/backoffice/account/current/')
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public updateCurrentUser(user) {
    return this.http.put<any>(environment.apiUrl + '/backoffice/account/', user)
      .pipe(
        tap(res => {
          this.updateCookies(
            res.username,
            res.language.locale,
            res.fullName
          );
          return res;
        }),
        catchError(res => this.helper.catchError(res))
      );
  }

  private updateCookies(username: string, locale: string, fullName: string) {
    this.cookieManager.setCookie('username', username);
    this.cookieManager.setCookie('locale', locale);
    this.cookieManager.setCookie('fullName', fullName);
  }

  public manageUser(systemUser, isEdit, id = null) {
    delete systemUser.fullName;
    delete systemUser.exactId;
    if (isEdit) {
      return this.http.put(environment.apiUrl + '/backoffice/users/backoffice/' + id, systemUser)
        .pipe(catchError(res => this.helper.catchError(res)));
    }
    return this.http.post(environment.apiUrl + '/backoffice/users/backoffice/', systemUser)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  // US-720: only the order/quote VAT-calc dealer picker (manage-order) needs
  // country.dealerVat/customerVat on top of id/companyName, so it stays on this
  // route. Every other dealer-picker caller should use getDealerUsersDropdown()/
  // getAllDealerUsersDropdown() below, which only need id/companyName.
  public getDealerUsers(gridData) {
    return this.http.get(environment.apiUrl + '/backoffice/users/dealer/?' + gridData.toString())
      .pipe(map(rsp => rsp['data']), catchError(res => this.helper.catchError(res)));
  }

  // Get all dealers include inactive or on-hold users
  public getAllDealerUsers(gridData) {
    return this.http.get(environment.apiUrl + '/backoffice/users/dealer/?' + gridData.toString() + '&status=all')
      .pipe(map(rsp => rsp['data']), catchError(res => this.helper.catchError(res)));
  }

  // US-720: slim id+companyName-only variant of getDealerUsers(), for dealer
  // pickers that don't need country VAT fields.
  public getDealerUsersDropdown(gridData) {
    return this.http.get(environment.apiUrl + '/backoffice/users/dealer/dropdown/?' + gridData.toString())
      .pipe(map(rsp => rsp['data']), catchError(res => this.helper.catchError(res)));
  }

  // US-720: slim id+companyName-only variant of getAllDealerUsers().
  public getAllDealerUsersDropdown(gridData) {
    return this.getDealerUsersDropdown(gridData.toString() + '&status=all');
  }

  public getDealerUser(userId) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/users/dealer/' + userId)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getDealerEmployees(gridData, params) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/users/dealer/' + params.id + '/employees/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getDealerContacts(gridData, params) {
    return this.http.get<any>(environment.apiUrl + '/backoffice/users/dealer/' + params.id + '/contacts/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public manageDealerUser(systemUser, isEdit, id = null) {
    delete systemUser.fullName;
    if (isEdit) {
      return this.http.put(environment.apiUrl + '/backoffice/users/dealer/' + id, systemUser)
        .pipe(catchError(res => this.helper.catchError(res)));
    }
    return this.http.post(environment.apiUrl + '/backoffice/users/dealer/', systemUser)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public manageDeliveryDealerUser(systemUser, isEdit, id = null) {
    delete systemUser.fullName;
    if (isEdit) {
      return this.http.put(environment.apiUrl + '/backoffice/users/dealer/delivery/' + id, systemUser)
        .pipe(catchError(res => this.helper.catchError(res)));
    }
    return this.http.post(environment.apiUrl + '/backoffice/users/dealer/delivery', systemUser)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public softDeleteDealer(id) {
    return this.http.delete(environment.apiUrl + '/backoffice/users/dealer/' + id)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public softDeleteEmployeeUser(id, newAdmin = null) {
    return this.http.post(environment.apiUrl + '/backoffice/users/dealer/' + id + '/employee-delete', {replace: newAdmin})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getEmployeeUserArchive(gridData , params) {
    return this.http.get(environment.apiUrl + '/backoffice/users/dealer/' + params.id + '/employee-archive/?' + gridData.toString())
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public readdDealerUser(id) {
    return this.http.put(environment.apiUrl + '/backoffice/users/dealer/' + id + '/reAdd', {id: id})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public updateDealerPoint(systemUser, id = null) {
    return this.http.put(environment.apiUrl + '/backoffice/users/dealer/' + id + '/dealer-point', systemUser)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public updateDealerStatus(status, id) {
    return this.http.put(environment.apiUrl + '/backoffice/users/dealer/' + id + '/status', {status: status})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public updateDealerBundleInvoice(bundleStatus, id) {
    return this.http.put(environment.apiUrl + '/backoffice/users/dealer/' + id + '/bundle-invoice', {bundle: bundleStatus})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  updateDealerAdvancedCall(value, id) {
    return this.http.put(environment.apiUrl + '/backoffice/users/dealer/' + id + '/advance-call', {advance: value})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  updateDealerDefaultDelivery(value, id) {
    return this.http.put(environment.apiUrl + '/backoffice/users/dealer/' + id + '/default-delivery', {defaultDelivery: value})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  updateDealerPointStatus(value, id) {
    return this.http.put(environment.apiUrl + '/backoffice/users/dealer/' + id + '/point-status', {dealerPointEnabled: value})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public readdEmployeeUser(id) {
    return this.http.put(environment.apiUrl + '/backoffice/users/dealer/' + id + '/employee-reAdd', {id: id})
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getUserRoles() {
    return this.http.get<any>(environment.apiUrl + '/backoffice/account/roles')
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public getBackofficeRoles() {
    return this.http.get<any>(environment.apiUrl + '/backoffice/account/backoffice-roles')
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  /**
   * List DealerType
   *
   * @returns {Observable<DealerTypeListResponse>}
   * @param {HttpParams} params
   */
  public DealerTypeList(
    params = new HttpParams,
  ): Observable<DealerTypeListResponse> {
    const url = `${environment.apiUrl}/backoffice/dealer-type`;
    return this.http.get<DealerTypeListResponse>(
      url,
      {params: params}
    );
  }

  public manageDeliveryContact(deliveryContact, isEdit, id = null) {
    if (isEdit) {
      return this.http.put(environment.apiUrl + '/backoffice/users/dealer/delivery-contact-edit/' + id, deliveryContact)
        .pipe(catchError(res => this.helper.catchError(res)));
    }
    return this.http.post(environment.apiUrl + '/backoffice/users/dealer/delivery-contact-add', deliveryContact)
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public connectToExact() {
    return this.http.get<any>(environment.apiUrl + '/exact')
      .pipe(catchError(res => this.helper.catchError(res)));
  }

  public activateTwoFactorAuthenticate(): Observable<any> {
    return this.http.post<ActivateTwoFactorResponse>(environment.apiUrl + '/users/activate-2fa', {})
      .pipe(
        tap(res => res),
        catchError(res => this.helper.catchError(res))
      );
  }

  public verifyTwoFactorAuthenticate(auth_code: string): Observable<any> {
    return this.http.post(environment.apiUrl + '/users/verify-2fa', {auth_code})
      .pipe(
        tap(res => res),
        catchError(res => {
          if (res.status !== 400) {
            return this.helper.catchError(res);
          } else {
            return throwError(res);
          }
        })
      );
  }

  public transferOrders(params) {
    return this.http.post(environment.apiUrl + '/backoffice/users/backoffice/transfer-orders', params)
      .pipe(catchError(res => this.helper.catchError(res)));
  }
}
